// path: src/FriendProfile.js
// Read-only player profile (stats, achievements, full collection grid).
// Opened from Friends and Leaderboards.

import React, { useMemo, useState } from "react";
import characters from "./Characters.json";
import Collection from "./Collection";
import Achievements from "./Achievements";
import { formatGid, isValidGid } from "./utils/gid";
import { getCardImageSrc } from "./utils/cardImages";
import {
    getAvatarFrameClassName,
    getCosmeticById,
} from "./utils/cosmetics";
import AvatarFrameDecor from "./Components/cosmetics/AvatarFrameDecor";
import "./App.css";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "collection", label: "Collection" },
    { id: "achievements", label: "Achievements" },
];

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const FriendProfile = ({
    profile = null,
    loading = false,
    error = "",
    totalCards = characters.length,
}) => {
    const [tab, setTab] = useState("overview");

    const profileCard = useMemo(() => {
        if (!profile?.profileImageId && profile?.profileImageId !== 0) {
            return null;
        }

        const selectedId = normalizeId(profile.profileImageId);
        return (
            characters.find((card) => normalizeId(card.id) === selectedId) ||
            null
        );
    }, [profile]);

    if (loading) {
        return (
            <div className="friendProfileScreen">
                <div className="friendsHeader">
                    <h1 className="impactFont pageTitle friendsTitle">Player Profile</h1>
                </div>
                <div className="friendsPanel">
                    <div className="friendsBody">Loading player profile…</div>
                </div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="friendProfileScreen">
                <div className="friendsHeader">
                    <h1 className="impactFont pageTitle friendsTitle">Player Profile</h1>
                </div>
                <div className="friendsPanel">
                    <div className="friendsBody friendsBody--error">
                        {error || "Player profile unavailable."}
                    </div>
                </div>
            </div>
        );
    }

    const displayName = profile.username || "Player";
    const gidText = isValidGid(profile.gid) ? formatGid(profile.gid) : "";

    return (
        <div className="friendProfileScreen">
            <div className="friendsHeader">
                <div>
                    <h1 className="impactFont pageTitle friendsTitle">{displayName}</h1>
                    {gidText ? (
                        <div className="friendProfileGid">GID {gidText}</div>
                    ) : null}
                </div>
            </div>

            <div className="friendsTabs" role="tablist" aria-label="Player profile sections">
                {TABS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === item.id}
                        className={`friendsTab ${
                            tab === item.id ? "friendsTab--active" : ""
                        }`}
                        onClick={() => setTab(item.id)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <div className="friendsPanel friendProfileOverview">
                    <div className="friendProfileHero">
                        <div
                            className={[
                                "profileImageFrame",
                                getAvatarFrameClassName(
                                    profile?.equippedAvatarFrameId
                                ),
                                !profile?.equippedAvatarFrameId
                                    ? "profileImageFrame--bare"
                                    : "",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        >
                            <div className="profileImageFrame__media">
                                {profileCard ? (
                                    <img
                                        src={getCardImageSrc(profileCard)}
                                        alt={`${displayName} profile`}
                                        className="profileImage"
                                    />
                                ) : (
                                    <div className="profileImagePlaceholder">
                                        No profile picture
                                    </div>
                                )}
                            </div>
                            <AvatarFrameDecor
                                visualKey={
                                    getCosmeticById(
                                        profile?.equippedAvatarFrameId
                                    )?.visualKey
                                }
                            />
                        </div>
                        <div className="friendProfileHeroText">
                            <div className="friendProfileName">{displayName}</div>
                            {profile?.equippedTitle ? (
                                <div className="friendProfileTitle">
                                    {profile.equippedTitle}
                                </div>
                            ) : null}
                            {profileCard ? (
                                <div className="friendProfileCardName">
                                    {profileCard.name}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <h2 className="friendsSectionTitle">Historical Stats</h2>
                    <div className="profileStats">
                        <div className="profileStat">
                            <div className="profileStatLabel">Games Played</div>
                            <div className="profileStatValue">
                                {profile.gamesPlayed || 0}
                            </div>
                        </div>
                        <div className="profileStat">
                            <div className="profileStatLabel">Cards Owned</div>
                            <div className="profileStatValue">
                                {profile.cardsOwned || 0}
                            </div>
                        </div>
                        <div className="profileStat">
                            <div className="profileStatLabel">Foils Found</div>
                            <div className="profileStatValue">
                                {profile.foilsFound || 0}
                            </div>
                        </div>
                        <div className="profileStat">
                            <div className="profileStatLabel">Highest Score</div>
                            <div className="profileStatValue">
                                {profile.topScore || 0}
                            </div>
                        </div>
                        <div className="profileStat">
                            <div className="profileStatLabel">Highest Level</div>
                            <div className="profileStatValue">
                                {profile.highestLevel || 0}
                            </div>
                        </div>
                        <div className="profileStat">
                            <div className="profileStatLabel">Longest Streak</div>
                            <div className="profileStatValue">
                                {profile.longestStreak || 0}
                            </div>
                        </div>
                    </div>

                    <div className="friendsHint">
                        Read-only friend view — collection and achievements update when
                        they sync their profile.
                    </div>
                </div>
            )}

            {tab === "collection" && (
                <Collection
                    unlockedIds={profile.collectionUnlocked || []}
                    foilUnlockedIds={profile.collectionFoilUnlocked || []}
                    embedded
                />
            )}

            {tab === "achievements" && (
                <Achievements
                    collectionUnlocked={profile.collectionUnlocked || []}
                    collectionFoilUnlocked={profile.collectionFoilUnlocked || []}
                    storyProgress={profile.storyProgress}
                    endlessStats={profile.endlessStats}
                    topScore={profile.topScore || 0}
                    gamesPlayed={profile.gamesPlayed || 0}
                    totalCards={totalCards}
                    embedded
                    hintText="Achievements calculated from this friend's published progress."
                />
            )}
        </div>
    );
};

export default FriendProfile;
